import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Table from 'react-bootstrap/Table';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { difference, isEmpty, uniqBy, cloneDeep } from 'lodash';
import AddSupportingDocuments from './AddSupportingDocuments';
import AddSupportingLink from './AddSupportingLink';
import EditSupportingDocuments from './EditSupportingDocuments';
import * as action from './store/SupportingDocument.action';
import * as bomAction from '../../Pages/Library/BomLibrary/store/BomLibrary.action';
import ConfirmationPopup from '../ModalPopup/ConfirmationPopup';
import FilePreview from '../FilePreview/FilePreview';
import AddToLibraries from '../SupportingDocuments/AddToLibraries';
import CustomTooltip from '../Tooltip/CustomTooltip';

const NON_PREVIEWABLE_EXTENSIONS = ['dxf', 'rul', 'wds'];
const BOM_SUPPORTING_DOC_MODULE = 'bom-supporting-doc';

const getFileExtension = (fileName) => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

/**
 * Merges supporting-document lists into one row per document. Rows are keyed by `_id`, so two
 * uploads that share a file name both stay visible. A colourway document picked from the
 * Colour Library (recognised by its `filePath`, which BOM document rows never carry) is dropped
 * only when a saved Colour row for the same colourway and file name is already listed, so
 * re-picking an attached colourway does not show its documents twice.
 * @param {...Array<object>} lists Document lists to merge, in display order.
 * @returns {Array<object>} The merged documents.
 */
export const mergeSupportingDocuments = (...lists) => {
  const colourKey = (doc) => `${doc?.colourWay ?? doc?.libId}|${doc?.fileName}`;
  const docs = uniqBy(lists.flat().filter(Boolean), '_id');
  const savedColourKeys = new Set(docs.filter((doc) => doc?.docType === 'Colour' && !doc?.filePath).map(colourKey));
  return docs.filter((doc) => !doc?.filePath || !savedColourKeys.has(colourKey(doc)));
};

const elsePart = (selectedIdArray, documents, setSelectedIdArray, setSelectAll) => {
  const selectedItems = [...selectedIdArray];
  if (!Array.isArray(documents)) {
    for (const item of documents) {
      const findItems = selectedItems.filter((val) => val === item._id);
      if (findItems.length === 0) {
        selectedItems.push(item._id);
      }
    }
    setSelectedIdArray(selectedItems);
  } else {
    const Selected = [];
    documents.forEach((ele) => {
      Selected.push(ele._id);
    });
    setSelectedIdArray([...Selected]);
    setSelectAll(true);
  }
};

const SupportingDocumentTable = forwardRef(({ data, emptyDocs, setFinalDocuments, referenceOptions, fromBomExpand }) => {
  const { t } = useTranslation('common');
  const { t: tBom } = useTranslation('modules/bom');
  const reducerData = useSelector((reducerState) => reducerState);
  const { ColorLibraryDetails, SupportingDocument, BomLibraryDetails, SystemMastersDetails } = reducerData;
  const { placement_master } = SystemMastersDetails;
  const { ColorLibList } = ColorLibraryDetails;
  const { supportingDocs } = SupportingDocument;
  const { colourDocs } = BomLibraryDetails;
  const dispatch = useDispatch();

  const [showUploadModal, setUploadModal] = useState(false);
  const [showUploadLinkModal, setUploadLinkModal] = useState(false);
  const [showEditModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [loader, setLoader] = useState(false);
  const [selectedDoc, setDoc] = useState(null);
  const [selectedIdArray, setSelectedIdArray] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isSelectAll, setSelectAll] = useState(false);
  const [showFilePreviewModal, setFilePreviewModal] = useState(false);
  const [selectedFileToPreview, setFileToPreview] = useState(null);
  const [codeForLibrary, setCodeForLibrary] = useState('');
  const [idForLibrary, setIdForLibrary] = useState(null);
  const [addTolibrary, setAddToLibrary] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSupportingDocuments = async () => {
    const params = {
      bomType: data.bomType,
      libId: data.libId ? data.libId : null,
      devOrderId: data.devOrderId ? data.devOrderId : null,
      bomId: data.bomId ? data.bomId : null,
    };
    await dispatch(action.getSupportingDocuments(params));
  };

  useEffect(() => {
    if (!isEmpty(supportingDocs) || !isEmpty(colourDocs) || !isEmpty(documents)) {
      setDocuments(mergeSupportingDocuments(supportingDocs, colourDocs, documents));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colourDocs, supportingDocs]);

  useEffect(() => {
    if (fromBomExpand && data?.bomType && data?.libId) {
      fetchSupportingDocuments();
    }
  }, [fromBomExpand, data?.bomType, data?.libId, data?.devOrderId, data?.bomId]);

  useEffect(() => {
    if (typeof setFinalDocuments === 'function' && !isEmpty(documents)) {
      setFinalDocuments([...documents]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useEffect(() => {
    if (!fromBomExpand) {
      fetchSupportingDocuments();
    }
    return async () => {
      setDocuments([]);
      await dispatch(action.unmountSupportingDocuments());
      await dispatch(bomAction.clearColourDocs());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const latestFetchDocsRef = useRef(fetchSupportingDocuments);
  latestFetchDocsRef.current = fetchSupportingDocuments;
  const lastUpload = reducerData?.uploader?.lastCompleted;

  useEffect(() => {
    if (!lastUpload?.seq) return;
    if (lastUpload.module !== BOM_SUPPORTING_DOC_MODULE) return;
    const currentDevOrderId = data?.devOrderId || null;
    if (lastUpload.devOrderId && currentDevOrderId && lastUpload.devOrderId !== currentDevOrderId) return;
    latestFetchDocsRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpload?.seq, lastUpload?.module, lastUpload?.devOrderId]);

  useEffect(() => {
    if (!isEmpty(supportingDocs)) {
      if (supportingDocs.length === documents.length) {
        const doc = cloneDeep(documents);
        const newArray = [];
        supportingDocs.forEach((ele) => {
          doc.forEach((element, ind) => {
            if (ele.fileName !== element.fileName) {
              const clonedArr = cloneDeep(documents);
              clonedArr[ind] = ele;
              newArray.push(ele);
            } else {
              newArray.push(ele);
            }
          });
        });
        setDocuments(mergeSupportingDocuments(newArray));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportingDocs]);

  useEffect(() => {
    if (emptyDocs) {
      setDocuments([]);
    }
  }, [emptyDocs]);

  const handleAddDocModal = (refresh) => {
    setUploadModal(!showUploadModal);
    if (refresh) {
      setSelectedIdArray([]);
      fetchSupportingDocuments();
    }
  };

  const handleAddDocLinkModal = (refresh) => {
    setUploadLinkModal(!showUploadLinkModal);
    if (refresh) {
      setSelectedIdArray([]);
      fetchSupportingDocuments();
    }
  };

  const handleEditModal = (refresh) => {
    setEditModal(!showEditModal);
    setEditData(null);
    refresh && fetchSupportingDocuments();
  };

  const editDoc = (doc) => {
    setEditData(doc);
    setEditModal(true);
  };

  const onSelectCheckBox = async (id) => {
    let selectedItems = [...selectedIdArray];
    const findItems = selectedItems.filter((item) => item === id);
    if (findItems.length > 0) {
      selectedItems = selectedItems.filter((item) => item !== id);
      setSelectedIdArray([...selectedItems]);
    } else {
      setSelectedIdArray([...selectedItems, id]);
    }
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      setSelectedIdArray([]);
    } else {
      elsePart(selectedIdArray, documents, setSelectedIdArray, setSelectAll);
    }
    setSelectAll(!isSelectAll);
  };

  useEffect(() => {
    if (Array.isArray(documents) && documents?.length > 0) {
      const stateIds = documents.map((item) => item._id);
      const selectedItems = [...selectedIdArray];
      const unSelectedItems = difference(stateIds, selectedItems);
      if (unSelectedItems.length > 0) {
        setSelectAll(false);
      } else {
        setSelectAll(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdArray]);

  const handleDeleteModal = () => {
    setShowConfirmationPopup(!showConfirmationPopup);
    setDoc(null);
  };

  const deleteDocument = (id) => {
    setDoc(id);
    setShowConfirmationPopup(!showConfirmationPopup);
  };

  const downloadDocument = (fileName, path) => {
    fetch(path)
      .then((response) => response.blob()) // Convert response to Blob
      .then((blob) => {
        const fileObjectURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = fileObjectURL;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileObjectURL); // Clean up
      })
      .catch((error) => console.error('Download error:', error));
  };

  const deleteById = async () => {
    setLoader(true);
    const params = data?.devOrderId ? { devOrderId: data?.devOrderId } : {};
    if (selectedDoc) {
      params.ids = [selectedDoc];
    } else {
      params.ids = [...selectedIdArray];
    }
    await dispatch(bomAction.clearColourDocs());
    const res = await dispatch(action.deleteSupportingDocuments(params, t));
    if (res) {
      setSelectedIdArray([]);
      setSelectAll(false);
      setShowConfirmationPopup(!showConfirmationPopup);
      if (res.payload && res.payload.length > 0) {
        res.payload.forEach((ele) => {
          documents.forEach((element, i) => {
            if (element._id === ele) {
              documents.splice(i, 1);
            }
          });
        });
        setDocuments(mergeSupportingDocuments(documents));
      }
      setLoader(false);
    }
  };

  const openFilePreview = (doc) => {
    const params = {
      fileName: doc.fileName,
      mimeType: doc.mimeType,
      path: doc.filePath ? doc.filePath : doc.path,
    };
    setFileToPreview(params);
    setFilePreviewModal(true);
  };

  const showCreatedandModified = (item) => {
    return (
      <button className="btn p-0" style={{ margin: '0' }} type="button">
        <div className="info-container testing-action-spaceremoval">
          <i className="icon-Information  f-18" />
          <div className="info-content">
            {item?.createdBy?.firstName && (
              <p className="text-left m-0">
                <strong>{t('SUPPORTING_DOCUMENTS_TABLE.CREATED')}</strong> : {`${item.createdBy?.firstName} ${item.createdBy?.lastName} | `}
                {moment(item.createdAt).format('DD-MMMM-YYYY, h:mm a')}
              </p>
            )}
            {item.createdBy?.firstName && ( // modifiedBy
              <p className="text-left m-0">
                <strong>{t('SUPPORTING_DOCUMENTS_TABLE.MODIFIED')}</strong> : {`${item.createdBy?.firstName} ${item.createdBy?.lastName} | `}
                {moment(item.updatedAt).format('DD-MMMM-YYYY, h:mm a')}
              </p>
            )}
          </div>
        </div>
      </button>
    );
  };

  const submitToLibrary = async (codeData) => {
    setIsLoading(true);
    const params = {
      docId: idForLibrary,
      docName: codeData,
    };
    await dispatch(action.AddColDocs(params, t));
    setIsLoading(false);
    toggleLibrary();
  };

  const AddToLib = (checkItem) => {
    if (Array.isArray(documents)) {
      const place = documents.findIndex((rost) => rost._id === checkItem._id);
      setCodeForLibrary(documents[place]?.fileName);
      setIdForLibrary(documents[place]?._id);
      setAddToLibrary(true);
    }
  };

  const toggleLibrary = () => {
    setAddToLibrary(!addTolibrary);
    setCodeForLibrary('');
    setIdForLibrary(null);
  };

  const colourName = (ele) => {
    const colour = ColorLibList.filter((elements) => elements._id === (ele?.colourWay ? ele?.colourWay : ele?.libId))[0];
    return `${colour?.name}`;
  };

  const togglePreview = async () => {
    setFilePreviewModal(!showFilePreviewModal);
  };

  const getPlacementName = (id) => {
    const option = placement_master?.length > 0 && placement_master?.find((option) => option?._id === id);
    return option?.name;
  };

  const viewOnly = !!data?.viewOnly;
  const isUploadDisabled = !data?.libId;

  return (
    <>
      <div className="d-flex suporting-table align-items-center">
        {!viewOnly && (
          <div className="side-icon supporting-doc-bom order-lg-last">
            <div className="d-flex">
              <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.UPLOAD_LINK')}>
                <button
                  aria-label="link"
                  type="button"
                  onClick={handleAddDocLinkModal}
                  className="btn d-inline-flex align-items-center mr-2 support-upload-button"
                >
                  <i className="icon-Link-Bold-2 f-24" />
                </button>
              </CustomTooltip>
              <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.UPLOAD_DOCUMENT')}>
                <button
                  aria-label="upload"
                  type="button"
                  onClick={handleAddDocModal}
                  disabled={isUploadDisabled}
                  className="btn d-inline-flex align-items-center mr-2 support-upload-button"
                >
                  <i className="icon-Upload-1 f-24" />
                </button>
              </CustomTooltip>
              {isUploadDisabled && (
                <CustomTooltip placement="top" tooltipText={tBom('UPLOAD_DISABLED_UNSAVED')}>
                  <span className="d-inline-flex align-items-center mr-2">
                    <i className="icon-Information f-18" />
                  </span>
                </CustomTooltip>
              )}

              {!isEmpty(selectedIdArray) && (
                <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.DELETE')}>
                  <button
                    aria-label="delete"
                    type="button"
                    disabled={isEmpty(selectedIdArray)}
                    onClick={() => {
                      deleteDocument(null);
                    }}
                    className="btn support-upload-button"
                  >
                    <i className="icon-Delete" />
                  </button>
                </CustomTooltip>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="p-0 overRight-x">
        <Table className="table p-0 common-custom-table">
          <thead className="light-grey text-center">
            <tr className="support-doc-head">
              {!viewOnly && !isEmpty(documents) && (
                <th className="w-50px common-data-head">
                  <div className="common-checkbox">
                    <input onChange={handleSelectAll} name="check_all" id="check_all" type="checkbox" checked={isSelectAll} disabled={isEmpty(documents)} />
                    <label htmlFor="check_all" className="cus-chklabel chklabel" />
                  </div>
                </th>
              )}
              <th className="w-350px common-data-head">{t('SUPPORTING_DOCUMENTS_TABLE.DOCUMENT_NAME')}</th>
              <th className="w-100px common-data-head">{t('SUPPORTING_DOCUMENTS_TABLE.TYPE')}</th>
              <th className="w-200px common-data-head">{t('SUPPORTING_DOCUMENTS_TABLE.QUALITY_COLOUR')}</th>
              <th className="w-150px common-data-head">{t('ADD_SUPPORTING_DOCUMENT.REFERENCE')}</th>
              <th className="w-150px common-data-head">{t('ADD_SUPPORTING_DOCUMENT.PLACEMENT')}</th>
              <th className="w-200px common-data-head" />
            </tr>
          </thead>

          <tbody className="tabbody">
            {!isEmpty(documents) &&
              uniqBy(documents, '_id') &&
              documents.map((doc, i) => (
                <tr key={doc?._id}>
                  {!viewOnly && (
                    <td>
                      <div className="common-checkbox">
                        <input
                          onChange={() => onSelectCheckBox(doc._id)}
                          name={`checks_${i}`}
                          id={`checks_${i}`}
                          type="checkbox"
                          checked={selectedIdArray.filter((val) => val === doc._id).length > 0}
                        />
                        <label htmlFor={`checks_${i}`} className="cus-chklabel chklabel" />
                      </div>
                    </td>
                  )}
                  <td className="text-center">{doc?.fileName}</td>
                  <td className="text-center">
                    <span className="text-capitalize">{doc?.documentType || '-'}</span>
                  </td>
                  <td className="text-center">
                    {doc?.docType ? doc?.docType : 'Quality'} {doc?.docType === 'Colour' ? `(${colourName(doc)})` : ''}
                  </td>
                  <td className="text-center">
                    <span className="text-capitalize">{doc?.reference?.name || '-'}</span>
                  </td>
                  <td className="text-center">
                    <span className="text-capitalize">{getPlacementName(doc?.placement) || '-'}</span>
                  </td>
                  <td className="text-right">
                    <div className="d-flex align-items-center justify-content-end bom-doc-table-action">
                      {!viewOnly && (
                        <CustomTooltip placement="top" tooltipText={t('SUPPORTING_DOCUMENTS_TABLE.EDIT')}>
                          <button aria-label="Edit" type="button" className="btn bom-action-icon" onClick={() => editDoc(doc)}>
                            <i className="icon-Edit-1 f-20" />
                          </button>
                        </CustomTooltip>
                      )}

                      {doc?.documentType !== 'link' && !NON_PREVIEWABLE_EXTENSIONS.includes(getFileExtension(doc?.fileName)) ? (
                        <CustomTooltip placement="top" tooltipText={t('SUPPORTING_DOCUMENTS_TABLE.FILE_PREVIEW')}>
                          <button type="button" aria-label="Preview" onClick={() => openFilePreview(doc)} className="btn bom-action-icon">
                            <i className="icon-Preview-Document" />
                          </button>
                        </CustomTooltip>
                      ) : doc?.documentType === 'link' ? (
                        <CustomTooltip placement="top" tooltipText={t('SUPPORTING_DOCUMENTS_TABLE.OPEN_URL')}>
                          <button aria-label="detail_page" type="button" className="btn bom-action-icon">
                            <a className="cursor-pointer text-dark text-decoration-none" target="_blank" href={doc?.linkUrl} rel="noreferrer">
                              <i className="icon-Link-Bold-2 f-22" />
                            </a>
                          </button>
                        </CustomTooltip>
                      ) : null}

                      {!data.fromBomLibrary && doc?.docType === 'Colour' && doc?.documentType !== 'link' && doc?.isSaved && (
                        <CustomTooltip placement="top" tooltipText={t('SUPPORTING_DOCUMENTS_TABLE.ADD_TO_LIBRARY')}>
                          <button type="button" aria-label="Library" className="btn bom-action-icon" onClick={() => AddToLib(doc)}>
                            <i className="icon-Library-1" />
                          </button>
                        </CustomTooltip>
                      )}
                      {doc?.documentType !== 'link' && (
                        <CustomTooltip placement="top" tooltipText={t('SUPPORTING_DOCUMENTS_TABLE.DOWNLOAD_TOOLTIP')}>
                          <button className="btn bom-action-icon" type="button" aria-label="download" onClick={() => downloadDocument(doc.fileName, doc.path)}>
                            <i className="icon-Download" />
                          </button>
                        </CustomTooltip>
                      )}
                      {!viewOnly && (
                        <CustomTooltip placement="top" tooltipText={t('SUPPORTING_DOCUMENTS_TABLE.DELETE')}>
                          <button type="button" aria-label="Delete" className="btn bom-action-icon" onClick={() => deleteDocument(doc._id)}>
                            <i className="icon-Delete" />
                          </button>
                        </CustomTooltip>
                      )}

                      {showCreatedandModified(doc)}
                    </div>
                  </td>
                </tr>
              ))}

            {isEmpty(documents) && (
              <tr>
                <td colSpan="6" className="text-center no-doc-upload">
                  {t('SUPPORTING_DOCUMENTS_TABLE.NO_DOCUMENT_UPLOADED')}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {!viewOnly && showUploadModal && (
        <AddSupportingDocuments
          show={showUploadModal}
          data={{ ...data }}
          handlePopup={handleAddDocModal}
          refreshDocs={fetchSupportingDocuments}
          referenceOptions={referenceOptions}
        />
      )}

      {!viewOnly && showUploadLinkModal && (
        <AddSupportingLink show={showUploadLinkModal} data={{ ...data }} handlePopup={handleAddDocLinkModal} referenceOptions={referenceOptions} />
      )}

      {!viewOnly && showEditModal && (
        <EditSupportingDocuments
          show={showEditModal}
          handlePopup={handleEditModal}
          data={editData}
          devOrderId={data?.devOrderId}
          referenceOptions={referenceOptions}
        />
      )}
      {!viewOnly && showConfirmationPopup && (
        <ConfirmationPopup
          onOpen={showConfirmationPopup}
          onConfirm={deleteById}
          loading={loader}
          title={t('SUPPORTING_DOCUMENTS_TABLE.DELETE')}
          message={t('SUPPORTING_DOCUMENTS_TABLE.MESSAGE')}
          toggle={handleDeleteModal}
        />
      )}
      {showFilePreviewModal && <FilePreview onOpen={showFilePreviewModal} fileObj={selectedFileToPreview} toggle={togglePreview} />}
      {addTolibrary && (
        <AddToLibraries onOpen={addTolibrary} Toggle={toggleLibrary} showLoader={isLoading} codeName={codeForLibrary} submitCode={submitToLibrary} />
      )}
    </>
  );
});
SupportingDocumentTable.displayName = 'SupportingDocumentTable';
export default SupportingDocumentTable;
